import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetTerminalsList,
  useConfigureSupport,
} from '@/modules/backoffice/services/terminals';

import {
  TerminalStatus,
} from '@/modules/backoffice/types/terminal';
import type {
  ITerminalListItem,
  TerminalFilterStatus,
  TerminalRecordStatus,
} from '@/modules/backoffice/types/terminal';

import { useTerminalFilters } from '../use-terminal-filters/use-terminal-filters';
import { useTerminalTabCounts } from '../use-terminal-tab-counts';
import { useQueryClient } from '@tanstack/react-query';
import { BACKOFFICE_QUERIES } from '@/modules/backoffice/services/queries';

import type {
  TerminalDrawerState,
} from './use-terminal-list-page.type';

import { ToastVariant } from '../use-terminal-filters';

const DEFAULT_PAGE_SIZE = 25;
const TOAST_DURATION_MS = 4000;

export const useTerminalListPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [status, setStatus] = useState<TerminalFilterStatus>(TerminalStatus.ALL);

  const [terminalDrawer, setTerminalDrawer] = useState<TerminalDrawerState>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);
  const [isBatchSuccessModalOpen, setIsBatchSuccessModalOpen] = useState(false);
  const [isBatchFailureModalOpen, setIsBatchFailureModalOpen] = useState(false);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  const openTerminalDrawer = (serialNumber: string) => {
    setTerminalDrawer({ serialNumber });
  };

  const closeTerminalDrawer = () => {
    setTerminalDrawer(null);
    setIsSuccessModalOpen(false);
    setIsFailureModalOpen(false);
  };

  const { mutateAsync: configureSupport, isPending: isConfiguring } = useConfigureSupport();

  const invalidateTerminalQueries = async (serialNumber: string) => {
    await queryClient.invalidateQueries({
      queryKey: [BACKOFFICE_QUERIES.TERMINALS],
    });
    await queryClient.invalidateQueries({
      queryKey: [BACKOFFICE_QUERIES.TERMINAL_DETAILS, serialNumber],
    });
  };

  const runTestAction = async (
    serialNumber: string,
    action: () => Promise<{ success: boolean; message: string }>,
  ) => {
    const result = await action();

    pushToast({
      variant: result.success ? 'success' : 'error',
      title: result.success ? t('backoffice.commonSuccess') : t('backoffice.commonError'),
      description: result.message,
    });

    if (result.success) {
      await invalidateTerminalQueries(serialNumber);
      closeTerminalDrawer();
    }
  };

  const handleApproveTest = async () => {
    if (!terminalDrawer) return;
    const { serialNumber } = terminalDrawer;
    await runTestAction(serialNumber, () =>
      configureSupport({ serialNumber, success: true, failureDescription: null }),
    );
  };

  const handleRejectTest = async (reason: string) => {
    if (!terminalDrawer) return;
    const { serialNumber } = terminalDrawer;
    await runTestAction(serialNumber, () =>
      configureSupport({ serialNumber, success: false, failureDescription: reason }),
    );
  };

  const runBatchTestAction = async (
    params: { success: boolean; failureDescription: string | null },
    toastKeys: { success: string; partial: string; error: string },
    onComplete: () => void,
  ) => {
    setIsBatchProcessing(true);
    const results = await Promise.allSettled(
      selectedRecords.map((terminal) =>
        configureSupport({ serialNumber: terminal.serialNumber, ...params }),
      ),
    );
    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failedCount = results.filter((r) => r.status === 'rejected').length;
    await queryClient.invalidateQueries({ queryKey: [BACKOFFICE_QUERIES.TERMINALS] });
    setSelectedKeys([]);
    onComplete();
    setIsBatchProcessing(false);
    if (failedCount === 0) {
      pushToast({ variant: 'success', title: t('backoffice.commonSuccess'), description: t(toastKeys.success) });
    } else if (successCount > 0) {
      pushToast({ variant: 'warning', title: t('backoffice.commonSuccess'), description: t(toastKeys.partial, { success: successCount, failed: failedCount }) });
    } else {
      pushToast({ variant: 'error', title: t('backoffice.commonError'), description: t(toastKeys.error) });
    }
  };

  const handleBatchApproveTest = () =>
    runBatchTestAction(
      { success: true, failureDescription: null },
      {
        success: 'backoffice.terminalsBatchApproveToastSuccess',
        partial: 'backoffice.terminalsBatchApproveToastPartial',
        error: 'backoffice.terminalsBatchApproveToastError',
      },
      () => setIsBatchSuccessModalOpen(false),
    );

  const handleBatchRejectTest = (reason: string) =>
    runBatchTestAction(
      { success: false, failureDescription: reason },
      {
        success: 'backoffice.terminalsBatchRejectToastSuccess',
        partial: 'backoffice.terminalsBatchRejectToastPartial',
        error: 'backoffice.terminalsBatchRejectToastError',
      },
      () => setIsBatchFailureModalOpen(false),
    );

  const [toasts, setToasts] = useState<
    Array<{
      id: string;
      variant: ToastVariant;
      title: string;
      description: string;
    }>
  >([]);

  const toastTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    return () => {
      toastTimeoutsRef.current.forEach((id) => clearTimeout(id));
    };
  }, []);

  const pushToast = (toast: {
    variant: ToastVariant;
    title: string;
    description: string;
  }) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

    setToasts((prev) => [...prev, { ...toast, id }]);

    const timeoutId = setTimeout(() => {
      setToasts((prev) => prev.filter((item) => item.id !== id));
      toastTimeoutsRef.current.delete(timeoutId);
    }, TOAST_DURATION_MS);

    toastTimeoutsRef.current.add(timeoutId);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  };

  const filters = useTerminalFilters({
    onToast: pushToast,
  });

  const [quickSearchSerial, setQuickSearchSerialState] = useState('');

  const setQuickSearchSerial = (value: string) => {
    setQuickSearchSerialState(value);
    setPage(1);
  };

  const serialNumbersParam = quickSearchSerial.trim()
    ? [quickSearchSerial.trim()]
    : filters.serialNumbersFilter;

  const sharedFilters = {
    barteCompanyIds: filters.payfacIdFilter,
    companyName: filters.payfacNameFilter,
    barteSellerIds: filters.sellerIdFilter,
    sellerName: filters.sellerNameFilter,
    serialNumbers: serialNumbersParam,
  };

  const { data, isLoading } = useGetTerminalsList({
    page: page - 1,
    limit: pageSize,
    status: status === TerminalStatus.ALL ? undefined : [status as TerminalRecordStatus],
    ...sharedFilters,
  });

  const terminals = useMemo(() => data?.data?.data ?? [], [data]);
  const selectedRecords = useMemo(
    () => terminals.filter((terminal) => selectedKeys.includes(terminal.id)),
    [terminals, selectedKeys],
  );
  const clearSelection = useCallback(() => setSelectedKeys([]), []);
  const pageable = data?.data?.pageable;

  const tabCounts = useTerminalTabCounts(sharedFilters);

  const handleChangeStatus = (newStatus: TerminalFilterStatus) => {
    setStatus(newStatus);
    setPage(1);
    setSelectedKeys([]);
  };

  const handleChangePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
    setSelectedKeys([]);
  };

  const handleCopySerialNumber = async (
    event: React.MouseEvent,
    serialNumber: string,
  ) => {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(serialNumber);
      pushToast({
        variant: 'success',
        title: t('backoffice.commonCopied'),
        description: t('backoffice.terminalsListCopySerialSuccess', {
          serialNumber,
        }),
      });
    } catch (error) {
      console.error('Erro ao copiar serial number', error);
      pushToast({
        variant: 'error',
        title: t('backoffice.commonError'),
        description: t('backoffice.terminalsListCopySerialError'),
      });
    }
  };

  const handleApplyFilters = () => {
    filters.handleApplyFilters();
    setPage(1);
  };

  const handleClearAllFilters = () => {
    filters.handleClearAllFilters();
    setPage(1);
  };

  const handleRemoveAppliedFilter = (
    key: keyof typeof filters.appliedFilters,
  ) => {
    filters.removeAppliedFilter(key);
    setPage(1);
  };


  const fakeData: ITerminalListItem[] = Array.from({ length: pageSize }).map(
    (_, index) => ({
      id: index,
      serialNumber: '',
      companyUuid: null,
      sellerId: null,
      status: TerminalStatus.ACTIVE,
      createdAt: null,
      createdBy: null,
      updatedAt: null,
      updatedBy: null,
      posCompanyName: '',
      companyId: null,
      posCompanyId: null,
      posSellerId: null,
      posSellerName: '',
      posSellerExternalId: '',
      posTerminalExternalId: '',
      transactionalStatus: null,
      lastTransactionAt: null,
      linkedAt: null,
    }),
  );

  return {
    isLoading,
    terminals,
    pageable,
    fakeData,

    page,
    setPage,
    pageSize,
    setPageSize: handleChangePageSize,

    status,
    tabCounts,
    handleChangeStatus,

    selectedKeys,
    setSelectedKeys,

    filters: {
      ...filters,
      handleApplyFilters,
      handleClearAllFilters,
      handleRemoveAppliedFilter,
      quickSearchSerial,
      setQuickSearchSerial,
    },

    selectedRecords,
    clearSelection,

    toasts,
    pushToast,
    removeToast,

    handleCopySerialNumber,

    terminalDrawer,
    openTerminalDrawer,
    closeTerminalDrawer,
    isSuccessModalOpen,
    openSuccessModal: () => setIsSuccessModalOpen(true),
    closeSuccessModal: () => setIsSuccessModalOpen(false),
    isFailureModalOpen,
    openFailureModal: () => setIsFailureModalOpen(true),
    closeFailureModal: () => setIsFailureModalOpen(false),
    handleApproveTest,
    handleRejectTest,
    isApproving: isConfiguring,
    isRejecting: isConfiguring,

    isBatchSuccessModalOpen,
    openBatchSuccessModal: () => setIsBatchSuccessModalOpen(true),
    closeBatchSuccessModal: () => setIsBatchSuccessModalOpen(false),
    isBatchFailureModalOpen,
    openBatchFailureModal: () => setIsBatchFailureModalOpen(true),
    closeBatchFailureModal: () => setIsBatchFailureModalOpen(false),
    handleBatchApproveTest,
    handleBatchRejectTest,
    isBatchProcessing,
  };
};